<template>
  <div class="n-conversation-thread" ref="scrollContainer">
    <div class="n-conversation-thread__container">
      <div class="n-conversation-thread__message-list">
        <transition-group name="transitions" tag="span">
          <div
            v-for="(m, index) in messages"
            :key="index"
            class="n-conversation-thread__message-row transition-item"
            :class="[`n-conversation-thread__message-row--${getMessagePosition(m)}`]"
          >
            <div
              class="n-conversation-thread__message-divider"
              v-if="shouldShowTimestampDivider(m, messages[index - 1])"
            >
              {{ formatTimestampDivider(m.timestampUtc) }}
            </div>
            <div class="n-conversation-thread__message-divider--whisper" v-if="m.type === 'whisper'">
              <span v-html="getFormattedMessage(m.text)" />
            </div>
            <NTextMessage
              v-if="m.type === 'text'"
              :message="m"
              :outboundMessageColour="outboundMessageColour"
              :tooltipPosition="getMessagePosition(m) === 'right' ? 'left' : 'right'"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
            />
            <NAppMessage
              v-if="m.type === 'app'"
              :message="m"
              :readonly="readonlyApps"
              :brandColour="outboundMessageColour"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
              :position="getMessagePosition(m)"
            />
            <NImageMessage
              v-if="m.type === 'image'"
              :message="m"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
              :tooltipPosition="getMessagePosition(m) === 'right' ? 'left' : 'right'"
            />
            <NVideoMessage
              v-if="m.type === 'video'"
              :message="m"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
              :tooltipPosition="getMessagePosition(m) === 'right' ? 'left' : 'right'"
            />
            <NAudioMessage
              v-if="m.type === 'audio'"
              :message="m"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
              :tooltipPosition="getMessagePosition(m) === 'right' ? 'left' : 'right'"
            />
            <NFileMessage
              v-if="m.type === 'file'"
              :message="m"
              :outboundMessageColour="outboundMessageColour"
              :displayAvatar="displayAvatar"
              :enableAvatarTooltip="enableAvatarTooltip"
              :tooltipPosition="getMessagePosition(m) === 'right' ? 'left' : 'right'"
            />
          </div>
          <div
            class="n-conversation-thread__quick-replies transition-item"
            :key="'quick-replies'"
            v-if="shouldShowQuickReplies()"
            :class="[
              `n-conversation-thread__quick-replies--${
                getMessagePosition(messages[messages.length - 1]) === 'right' ? 'left' : 'right'
              }`,
            ]"
          >
            <QuickReplies
              :actions="messages[messages.length - 1].actions"
              :messageColour="outboundMessageColour"
              @sendQuickReply="sendQuickReply"
            />
          </div>
        </transition-group>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import moment from 'moment';
import NIcon from '../../brand/icon/icon.vue';
import NTextMessage from './text-message.vue';
import NImageMessage from './image-message.vue';
import NVideoMessage from './video-message.vue';
import NAudioMessage from './audio-message.vue';
import NFileMessage from './file-message.vue';
import { Message, TextMessage } from './message';
import NAppMessage from './app-message.vue';
import QuickReplies from './quick-replies.vue';
import { MessageContent } from './messageContent';
import { PropType } from 'vue';

defineOptions({
  name: 'n-conversation-thread',
});

const props = defineProps({
  outboundMessageColour: {
    type: String,
    default: '#062b80',
  },
  messages: {
    type: Array as PropType<Message[]>,
    required: true,
  },
  displayAvatar: {
    type: Boolean,
    default: false,
  },
  enableAvatarTooltip: {
    type: Boolean,
    default: false,
  },
  readonlyApps: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: 'sendQuickReply', quickReply: any): void;
}>();

const scrollContainer = ref<HTMLDivElement | null>(null);
const isScrolledToBottom = ref(true); // Default to true to scroll to bottom initially

function shouldShowTimestampDivider(current: Message, previous: Message) {
  if (!current || !previous) {
    return false;
  }
  const currentDate = moment(current.timestampUtc);
  const previousDate = moment(previous.timestampUtc);
  return currentDate.diff(previousDate, 'hours', false) !== 0;
}

function formatTimestampDivider(timestamp: Date): string {
  return moment(timestamp).format('MMM D, YYYY, h:mm A');
}

function shouldShowQuickReplies() {
  const lastMessage = props.messages[props.messages.length - 1] as TextMessage;
  return lastMessage && lastMessage.actions && lastMessage.actions.length;
}

function sendQuickReply(quickReply: any) {
  emit('sendQuickReply', quickReply);
}

function getMessagePosition(m: TextMessage): 'right' | 'left' {
  return m.direction === 'outbound' ? 'right' : 'left';
}

function getFormattedMessage(message: string): string {
  return new MessageContent(message).EscapeHtml().ProcessUrls().NewLinesToBreaks().Value;
}

function scrollToBottom(): void {
  if (!scrollContainer.value) return;
  scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
  isScrolledToBottom.value = true;
}

// Watch for new messages and scroll to bottom if at the bottom
watch(
  () => props.messages.length,
  async () => {
    await nextTick(); // Wait for DOM update
    if (isScrolledToBottom.value) {
      scrollToBottom();
    }
  },
);

// Update isScrolledToBottom based on scroll position
function handleScroll() {
  if (!scrollContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  isScrolledToBottom.value = Math.abs(scrollHeight - scrollTop - clientHeight) < 10; // Allow small margin
}

onMounted(() => {
  scrollToBottom();
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped lang="scss">
@use '../../../styles/animations' as *;

.n-conversation-thread__container {
  display: flex;
  flex-direction: column;
  bottom: 0;
  width: 100%;
  min-width: 100px;
  top: 0;
  justify-content: space-between;
}

.n-conversation-thread {
  position: relative;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-color: #eee transparent;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;

  /* width */
  &::-webkit-scrollbar {
    width: 12px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #eee;
    border-radius: 1em;
    border: 3px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #ccc;
    border: 3px solid rgba(0, 0, 0, 0);
    background-clip: padding-box;
  }
}

.n-conversation-thread__message-list {
  bottom: 0;
  width: 100%;
  max-height: 100%;

  // Rendered by the transition-group
  > span {
    display: flex;
    flex-direction: column;
    padding-top: 1em;
    padding-bottom: 1em;
  }

  &.justify-center {
    justify-content: center;
  }

  .transitions-leave-active {
    opacity: 0;
    height: 0px;
    margin-bottom: 0px !important;
    overflow: hidden;
  }
  .transitions-enter-active {
    animation: bounce 100ms;
  }

  .transition-item.n-conversation-thread__message-row--right {
    transform-origin: 100% -25%;
    justify-content: flex-end;
  }

  .transition-item.n-conversation-thread__message-row--left {
    transform-origin: -100% -25%;
  }

  .n-conversation-thread__message-row {
    flex: 1 0 auto;
    position: relative;
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    -webkit-flex: 0 1 auto;
    margin-bottom: 0.75em;
  }
}

.n-conversation-thread__quick-replies {
  display: flex;
  width: 100%;

  &--right {
    justify-content: flex-end;
  }
}

.n-conversation-thread__message-divider {
  text-align: center;
  font-size: smaller;
  padding: 1em 0;
  flex: 1 0 100%;

  &--whisper {
    font-style: italic;
    flex: 1 0 auto;
    padding: 0;
    text-align: center;
    max-width: 100%;
  }
}
</style>
